import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingOverlay, useAlert } from '../../components';
import { getBankDetails, saveBankDetails } from '../../services/firestoreService';
import { isValidAccountNumber, isValidIFSC } from '../../utils/helpers';
import './BankDetailsScreen.css';

export const BankDetailsScreen = () => {
    const navigate = useNavigate();
    const alert = useAlert();

    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        loadBankDetails();
    }, []);

    const loadBankDetails = async () => {
        try {
            const data = await getBankDetails();
            if (data) {
                setAccountHolderName(data.accountHolderName || '');
                setBankName(data.bankName || '');
                setAccountNumber(data.accountNumber || '');
                setIfscCode(data.ifscCode || '');
            } else {
                setIsEditing(true);
            }
        } catch (error) {
            console.error('Error loading bank details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!accountHolderName.trim() || !bankName.trim() || !accountNumber || !ifscCode) {
            alert.showError('Error', 'Please fill all fields');
            return;
        }

        if (!isValidAccountNumber(accountNumber)) {
            alert.showError('Error', 'Invalid account number');
            return;
        }

        if (!isValidIFSC(ifscCode)) {
            alert.showError('Error', 'Invalid IFSC code');
            return;
        }

        setSaving(true);
        try {
            await saveBankDetails(accountHolderName, accountNumber, ifscCode.toUpperCase(), bankName);
            alert.showSuccess('Success', 'Bank details saved successfully');
            setIsEditing(false);
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to save bank details');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading bank details..." />;
    }

    return (
        <div className="bank-account-screen">
            <div className="bank-header-nav">
                <button className="back-btn-bank" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="bank-title-nav">BANK ACCOUNT</h1>
            </div>

            <div className="bank-content-area">
                <div className="withdrawal-account-card">
                    <div className="bank-icon-box">
                        <span className="material-icons">account_balance</span>
                    </div>
                    <div className="bank-card-text">
                        <h3 className="bank-card-title">WITHDRAWAL ACCOUNT</h3>
                        <p className="bank-card-subtitle">FUNDS WILL BE SENT HERE</p>
                    </div>
                </div>

                <div className="bank-form">
                    <div className="bank-input-group">
                        <label className="bank-input-label">ACCOUNT HOLDER NAME</label>
                        <input
                            type="text"
                            className="bank-input-field"
                            placeholder="As per bank records"
                            value={accountHolderName}
                            onChange={(e) => setAccountHolderName(e.target.value)}
                            disabled={!isEditing && accountHolderName}
                        />
                    </div>

                    <div className="bank-input-group">
                        <label className="bank-input-label">BANK NAME</label>
                        <input
                            type="text"
                            className="bank-input-field"
                            placeholder="e.g. State Bank of India"
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            disabled={!isEditing && bankName}
                        />
                    </div>

                    <div className="bank-input-group">
                        <label className="bank-input-label">ACCOUNT NUMBER</label>
                        <input
                            type="text"
                            className="bank-input-field"
                            placeholder="Enter full account number"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                            disabled={!isEditing && accountNumber}
                        />
                    </div>

                    <div className="bank-input-group">
                        <label className="bank-input-label">IFSC CODE</label>
                        <input
                            type="text"
                            className="bank-input-field"
                            placeholder="SBIN0001234"
                            value={ifscCode}
                            onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                            disabled={!isEditing && ifscCode}
                        />
                    </div>

                    {isEditing ? (
                        <button className="bank-save-btn" onClick={handleSave} disabled={saving}>
                            {saving ? 'SAVING...' : 'SAVE DETAILS'}
                        </button>
                    ) : (
                        <button className="bank-save-btn outline" onClick={() => setIsEditing(true)}>
                            EDIT DETAILS
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BankDetailsScreen;
